import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The width in px of an element, kept up to date with a ResizeObserver. Put the ref on the
 * element. 0 until the first measure, and in an environment with no ResizeObserver (the tests).
 */
export function useElementWidth<T extends HTMLElement>(): [(node: T | null) => void, number] {
  const [width, setWidth] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (node === null || typeof ResizeObserver === 'undefined') {
      return;
    }
    observer.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) {
        setWidth(Math.floor(entry.contentRect.width));
      }
    });
    observer.current.observe(node);
    setWidth(Math.floor(node.getBoundingClientRect().width));
  }, []);
  useEffect(() => () => observer.current?.disconnect(), []);
  return [ref, width];
}
